import { stdObject } from "../../../../data/types/stdObject";
import { colors } from "../../../../data/colors";
import { useMainContext } from "../../../../context/MainContext";
import ServiceCard from "./ServiceCard";
import BasicButton from "../../../../components/BasicButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useTranslation } from "react-i18next";
import convertDate from "../../../../utils/convertDate";
import { isInWeek } from "./calendarFunctions";

export default function Calendar({ params }: stdObject) {
  const {
    selected,
    setSelected,
    selectedWeek,
    calendarView,
    secondFlex,
    calendarDays,
    isBigScreen,
    isMediumcreen,
  } = params;
  const { t } = useTranslation();
  const userAccess: stdObject = useSelector(
    (state: RootState) => state.data.userAccess,
  );
  const { calDocsList, createNewCalendar } = useMainContext();
  const createNew = userAccess.C.C1N;

  const buildGrid = (): Array<Array<stdObject>> => {
    if (!(calendarDays.days > 0)) return [];
    let weeks = Math.ceil((calendarDays.days + calendarDays.dayWeek) / 7);
    let outDay = !!calendarDays.dayWeek;
    let counter = calendarDays.dayWeek
      ? calendarDays.lastDays - calendarDays.dayWeek + 1
      : 1;
    let prefix = calendarDays.dayWeek
      ? calendarDays.previous
      : calendarDays.current;
    const grid: Array<Array<stdObject>> = [];
    for (let index = 0; index < weeks; index++) {
      const row: Array<stdObject> = [];
      for (let idx1 = 0; idx1 < 7; idx1++) {
        counter =
          (!index && counter > calendarDays.lastDays) ||
          (index && counter > calendarDays.days)
            ? 1
            : counter;
        if (index === 0 && calendarDays.dayWeek && counter === 1) {
          outDay = false;
          prefix = calendarDays.current;
        } else if (index > 0 && counter === 1) {
          outDay = true;
          prefix = calendarDays.next;
        }
        const cellId = `${prefix}-${counter < 10 ? "0" + counter : counter}`;
        row.push({ cellId, dayNumber: counter, outDay, weekday: idx1 });
        counter++;
      }
      grid.push(row);
    }
    return grid;
  };

  const grid = buildGrid();
  const dayCell =
    calendarView === "day"
      ? grid.flat().find((cell: stdObject) => cell.cellId === selected)
      : null;
  const weekRow =
    calendarView === "week"
      ? grid.find((row: Array<stdObject>) => row[0].cellId === selectedWeek)
      : null;

  const weekdayLabel = (index: number) =>
    isBigScreen
      ? t(`p.d${index}`)
      : isMediumcreen
        ? t(`p.d${index}s`)
        : t(`p.d${index}m`);

  const headerColumns = dayCell ? [dayCell.weekday] : [0, 1, 2, 3, 4, 5, 6];
  const bodyRows = dayCell ? [[dayCell]] : weekRow ? [weekRow] : grid;

  const renderCell = (cell: stdObject, key: number) => (
    <td
      key={key}
      className={"tableTd noSelect"}
      style={{
        backgroundColor:
          cell.cellId === selected || isInWeek(cell.cellId, selectedWeek)
            ? colors.lightBlue2
            : cell.outDay
              ? colors.lightGrey
              : colors.white,
        padding: isBigScreen ? 10 : 3,
      }}
    >
      <div className="dayGroup">
        <BasicButton
          onClick={
            createNew && isBigScreen
              ? () => createNewCalendar({ nextDate: cell.cellId })
              : () => {}
          }
          title={`${cell.dayNumber}`}
          color={cell.outDay || !createNew ? colors.darkGrey : colors.blue}
          LastIcon={createNew && isBigScreen ? AddCircleIcon : null}
          style={{ top: 5, left: 35 }}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
        }}
      >
        {calDocsList
          .filter(
            (e: stdObject) =>
              typeof e.data.FEC2SEV === "string" &&
              e.data.FEC2SEV.includes(cell.cellId),
          )
          .map((e: stdObject, index: number) => (
            <ServiceCard key={index} mainData={e} isBigScreen={isBigScreen} />
          ))}
      </div>
    </td>
  );

  return (
    <div className="listBox" style={{ position: "relative" }}>
      <div
        id="tableBox"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width:
            secondFlex.active && secondFlex.size === "h" && selected !== ""
              ? "60%"
              : "100%",
          height:
            secondFlex.active && secondFlex.size === "v" && selected !== ""
              ? "60%"
              : "100%",
          overflow: "auto",
        }}
      >
        <table
          id="calendarTable"
          className="calendarTable"
          cellSpacing={0}
          cellPadding={5}
          style={{ width: "100%", height: "100%" }}
        >
          <thead>
            <tr className="header">
              {headerColumns.map((colIndex) => (
                <th
                  className="noSelect"
                  key={colIndex}
                  style={{
                    backgroundColor: colors.blue,
                    width: dayCell ? "100%" : "14.29%",
                  }}
                >
                  {weekdayLabel(colIndex)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row: Array<stdObject>, index: number) => (
              <tr key={index}>
                {row.map((cell: stdObject, idx1: number) =>
                  renderCell(cell, idx1),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          right: 0,
          bottom: 0,
          width:
            isBigScreen || selected === ""
              ? "0%"
              : secondFlex.active && secondFlex.size === "h"
                ? "40%"
                : "100%",
          height:
            isBigScreen || selected === ""
              ? "0%"
              : secondFlex.active && secondFlex.size === "v"
                ? "40%"
                : "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            position: "relative",
            backgroundColor: colors.blue,
            color: colors.white,
          }}
        >
          <div style={{ paddingLeft: 10 }}>
            {convertDate({ info: selected, full: true, t })}
          </div>
          <div style={{ padding: 5, height: 25 }}>
            {createNew && !isBigScreen && (
              <AddCircleIcon
                onClick={() => createNewCalendar({ nextDate: selected })}
                style={{ marginLeft: 10, fontSize: 25 }}
                color={colors.blue}
              />
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            padding: 5,
            overflow: "auto",
          }}
        >
          {calDocsList
            .filter(
              (e: stdObject) =>
                typeof e.data.FEC2SEV === "string" &&
                e.data.FEC2SEV.includes(selected),
            )
            .map((e: stdObject, index: number) => (
              <ServiceCard key={index} mainData={e} isBigScreen={true} />
            ))}
        </div>
      </div>
    </div>
  );
}
